/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The AI Assistant chat panel, opened from the search bar.
 *
 * A position:fixed drawer on the right, so it is left alone by the 80% body
 * zoom (appScale.js): it is sized with top/bottom rather than viewport units.
 * It sits one layer above the softphone dock, which lives in the bottom-right
 * corner too.
 *
 * Read-only. The assistant's replies can carry links to the records it looked
 * at and, when the user asked for one, a button to open a report draft in the
 * builder or to create a dashboard. Nothing is saved without that click.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AddCommentOutlined,
  AutoAwesome,
  CloseRounded,
  DashboardCustomizeRounded,
  OpenInNewRounded,
  SendRounded,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useAiAssistant } from "context/AiAssistantContext";
import colors from "styles/tokens/colors";
import CreateWithAiDialog from "./CreateWithAiDialog";

const PANEL_WIDTH = 420;

const SUGGESTIONS = [
  "How is my pipeline looking this month?",
  "Which of my leads are new this week?",
  "Draft a report of open deals by stage",
];

// Where a record the assistant mentions lives. Matches the global search.
const RECORD_ROUTES = {
  leads: (id) => `/leads/${id}`,
  deals: (id) => `/deals/${id}`,
  accounts: (id) => `/accounts/${id}`,
  contacts: (id) => `/contacts/${id}`,
  orders: (id) => `/orders/${id}`,
  quotations: (id) => `/quote/${id}`,
  cases: (id) => `/cases/${id}`,
};

function Message({ message, onAction }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const mine = message.role === "user";

  const bubble = mine
    ? { bg: colors.brand.primary, fg: colors.background.white }
    : message.error
      ? { bg: colors.status.error.background, fg: colors.status.error.text }
      : {
          bg: isDark ? "rgba(255,255,255,0.06)" : colors.background.offWhite,
          fg: theme.palette.text.primary,
        };

  const records = (message.records || []).filter((r) => RECORD_ROUTES[r.type]).slice(0, 8);

  return (
    <Box display="flex" flexDirection="column" alignItems={mine ? "flex-end" : "flex-start"} mb={1.5}>
      <Box
        sx={{
          maxWidth: "88%",
          px: 1.5,
          py: 1,
          borderRadius: "12px",
          borderTopRightRadius: mine ? "4px" : "12px",
          borderTopLeftRadius: mine ? "12px" : "4px",
          backgroundColor: bubble.bg,
          color: bubble.fg,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}
        >
          {message.content}
        </Typography>
      </Box>

      {records.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={0.75} mt={0.75} maxWidth="88%">
          {records.map((record) => (
            <Chip
              key={`${record.type}-${record.id}`}
              size="small"
              variant="outlined"
              label={record.label || `${record.type} ${record.id}`}
              icon={<OpenInNewRounded sx={{ fontSize: 14 }} />}
              onClick={() => navigate(RECORD_ROUTES[record.type](record.id))}
              sx={{ maxWidth: 220 }}
            />
          ))}
        </Box>
      )}

      {(message.pendingActions || []).map((action, index) => (
        <MDButton
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          size="small"
          variant="outlined"
          color="info"
          sx={{ mt: 1 }}
          onClick={() => onAction(action)}
        >
          {action.type === "create_dashboard" ? (
            <DashboardCustomizeRounded sx={{ mr: 0.75 }} fontSize="small" />
          ) : (
            <OpenInNewRounded sx={{ mr: 0.75 }} fontSize="small" />
          )}
          {action.label || "Open"}
        </MDButton>
      ))}
    </Box>
  );
}

export default function AiAssistantPanel() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { open, close, reset, messages, sending, sendMessage, balance } = useAiAssistant();
  const [draft, setDraft] = useState("");
  const [dashboardPrompt, setDashboardPrompt] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!open) return null;

  const send = (text) => {
    const value = (text ?? draft).trim();
    if (!value) return;
    setDraft("");
    sendMessage(value);
  };

  const onAction = (action) => {
    if (action.type === "open_report_draft" && action.draft) {
      navigate("/reports/new", { state: { draft: action.draft } });
    } else if (action.type === "create_dashboard") {
      // The dashboard is only created from the dialog's own button.
      setDashboardPrompt(action.prompt || "");
    }
  };

  const border = theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : colors.callCenter.cardBorder;

  return (
    <>
      <Paper
        role="complementary"
        aria-label="AI Assistant"
        elevation={8}
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: { xs: "100%", sm: PANEL_WIDTH },
          zIndex: (t) => t.zIndex.drawer + 3,
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          borderLeft: `1px solid ${border}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          px={2}
          py={1.5}
          sx={{ borderBottom: `1px solid ${border}` }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              backgroundColor: colors.brand.primaryContainer,
              color: colors.brand.primary,
            }}
          >
            <AutoAwesome sx={{ fontSize: 18 }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              AI Assistant
            </Typography>
            {balance !== null && (
              <Typography variant="caption" color="text.secondary">
                {balance.toLocaleString()} credits left
              </Typography>
            )}
          </Box>
          <Tooltip title="New chat">
            <span>
              <IconButton size="small" onClick={reset} disabled={!messages.length || sending}>
                <AddCommentOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={close} aria-label="Close AI Assistant">
              <CloseRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box ref={listRef} flex={1} px={2} py={2} sx={{ overflowY: "auto" }}>
          {!messages.length && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Ask about your leads, deals and pipeline, or ask for a report or dashboard. I can
                only see what you can see, and I never change your records.
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                {SUGGESTIONS.map((text) => (
                  <Chip key={text} label={text} variant="outlined" onClick={() => send(text)} />
                ))}
              </Box>
            </Box>
          )}
          {messages.map((message) => (
            <Message key={message.id} message={message} onAction={onAction} />
          ))}
          {sending && (
            <Box display="flex" alignItems="center" gap={1} color="text.secondary">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Thinking…
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          component="form"
          display="flex"
          alignItems="flex-end"
          gap={1}
          px={2}
          py={1.5}
          sx={{ borderTop: `1px solid ${border}` }}
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={5}
            size="small"
            placeholder="Ask the AI Assistant…"
            value={draft}
            inputProps={{ maxLength: 4000 }}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <IconButton
            type="submit"
            color="info"
            disabled={sending || !draft.trim()}
            aria-label="Send"
          >
            <SendRounded />
          </IconButton>
        </Box>
      </Paper>

      <CreateWithAiDialog
        open={dashboardPrompt !== null}
        kind="dashboard"
        initialPrompt={dashboardPrompt || ""}
        onClose={() => setDashboardPrompt(null)}
      />
    </>
  );
}
